import { DsmButton, DsmGrid, DsmIcon } from "@dsm-dcs/design-system-react";
import { Dispatch, FC, SetStateAction, useState } from "react";
import ReactHookDsmRadioGroup, { RadioGroupOption } from "../../../../../modules/Helpers/ReactHookDsmRadioGroup";
import ReactHookDsmPicker from "../../../../../modules/Helpers/ReactHookDsmDatePicker";
import ReactHookDsmSelect from "../../../../../modules/Helpers/ReactHookDsmSelect2";
import RowSelectWithMetricsV2 from "../../../../../modules/Farms/Intervention/RowSelectWithMetricsV2";
import EditModeButtons from "../../../helpers/EditModeButtons";
import { FormType } from "../../common";
import { BeefInputBreeding, BeefInputGrowing } from "../../../../models/Baseline/BeefBaseline";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { AnimalProductivity } from "../../../../../../graphql/generated/blonk/cattle";
import { useFormContext } from "react-hook-form";
import { BeefIntervention } from "../../../../models/Intervention/BeefIntervention";
import { StageType } from "../../../../../../graphql/types";
import { isFieldFilled } from "../CommonDataParts/InterventionGenericInfoDialog";
import { Spacer } from "../../../helpers/Spacer";
import lookupValues from "../../../../helpers/lookupValues";
import DefinitionsDialog from "../../CommonDataParts/v2.0/DefinitionsDialog";

interface BeefInputProductivityComponentProps {
  formType: FormType;
  stageType: StageType;
  fieldItemPrefix: string;
  defaultStageData: BeefInputGrowing | BeefInputBreeding;
  isBatchGrowingOperation: boolean;
  isProductivityEdit: boolean;
  setIsProductivityEdit: Dispatch<SetStateAction<boolean>>;
  productivityCancelHandler: (isInnerCancel?: boolean) => void;
}

const BeefInputProductivityComponent: FC<BeefInputProductivityComponentProps> = ({
  formType,
  stageType,
  fieldItemPrefix,
  defaultStageData,
  isBatchGrowingOperation,
  isProductivityEdit,
  setIsProductivityEdit,
  productivityCancelHandler,
}) => {
  const intl = useIntl();
  const classes = processAndStageStylesV2() as CSSClassesList;
  const fc = useFormContext<BeefIntervention>();

  const [openDescriptionDialog, setOpenDescriptionDialog] = useState<boolean>(false);

  const isGrowingOperation = stageType === StageType.Growing;

  const batchOperationOptions: RadioGroupOption[] = [
    {
      value: "batchGrowing",
      label: intl.formatMessage({
        id: "SUSTELL.STAGE.BEEF.INPUT.GROWING_OPERATION_BATCH",
      }),
    },
    {
      value: "continousGrowing",
      label: intl.formatMessage({
        id: "SUSTELL.STAGE.BEEF.INPUT.GROWING_OPERATION_CONTINOUS",
      }),
    },
  ];

  const cattleProductivityOptions = () =>
    Object.entries(AnimalProductivity).map(([key, value]) => ({
      text: key,
      value,
    }));

  return (
    <>
      {openDescriptionDialog && (
        <DefinitionsDialog
          open
          prefix='SUSTELL.STAGE.DAIRY.INPUT.CATTLE_PRODUCTIVITY.DEFINITIONS.DAIRY_COW'
          itemHeader='SUSTELL.STAGE.DAIRY.INPUT.CATTLE_PRODUCTIVITY'
          items={lookupValues.cattleInputDefinitions}
          outroText={true}
          handleClose={() => setOpenDescriptionDialog(false)}
        />
      )}
      <DsmGrid className={classes.dsmGridTwoColumnNoRowSpacing}>
        {isGrowingOperation &&
          <>
            <ReactHookDsmRadioGroup
              name={`${fieldItemPrefix}.growingOperation`}
              label={`${intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.GROWING_OPERATION" })}`}
              tooltip={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.GROWING_OPERATION.TOOLTIP" })}
              defaultValue={(defaultStageData as BeefInputGrowing).growingOperation.toString()}
              options={batchOperationOptions}
              rightPx={isProductivityEdit ? null : "0px"}
              disabled
            />
            <div />
          </>
        }

        <ReactHookDsmPicker
          name={`${fieldItemPrefix}.startDate`}
          label={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.START_DATE" })}
          tooltip={intl.formatMessage({
            id: isBatchGrowingOperation
              ? "SUSTELL.STAGE.BEEF.INPUT.START_DATE.TOOLTIP"
              : "SUSTELL.STAGE.BEEF.INPUT.GROWING.START_DATE.TOOLTIP",
          })}
          defaultValue={defaultStageData.startDate.toString()}
          disabled
        />

        {(isGrowingOperation && isBatchGrowingOperation) && (
          <ReactHookDsmPicker
            name={`${fieldItemPrefix}.endDate`}
            label={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.END_DATE" })}
            tooltip={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.END_DATE.TOOLTIP" })}
            defaultValue={(defaultStageData as BeefInputGrowing).endDate.toString()}
            disabled
          />
        )}

        {!isProductivityEdit ? (
          <>
            {isFieldFilled(fc.getValues(`${fieldItemPrefix}.cattleProductivity`)) &&
              fc.getValues(`${fieldItemPrefix}.cattleProductivity`) !== defaultStageData.cattleProductivity ? (
              <RowSelectWithMetricsV2
                name={`${fieldItemPrefix}.cattleProductivity`}
                label={intl.formatMessage({ id: 'SUSTELL.STAGE.BEEF.INPUT.CATTLE_PRODUCTIVITY' })}
                tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.BEEF.INPUT.CATTLE_PRODUCTIVITY.TOOLTIP' })}
                options={cattleProductivityOptions()}
                baseline={defaultStageData.cattleProductivity}
                adornment=" "
                useStyle={false}
                disabled
              />
            ) : (
              <ReactHookDsmSelect
                name={`${fieldItemPrefix}.originalCattleProductivity`}
                label={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.CATTLE_PRODUCTIVITY" })}
                tooltip={intl.formatMessage({ id: "SUSTELL.STAGE.BEEF.INPUT.CATTLE_PRODUCTIVITY.TOOLTIP" })}
                defaultValue={defaultStageData.cattleProductivity}
                options={cattleProductivityOptions()}
                disabled
              />
            )}
          </>
        ) : (
          <>
            <RowSelectWithMetricsV2
              name={`${fieldItemPrefix}.cattleProductivity`}
              label={intl.formatMessage({ id: 'SUSTELL.STAGE.BEEF.INPUT.CATTLE_PRODUCTIVITY' })}
              tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.BEEF.INPUT.CATTLE_PRODUCTIVITY.TOOLTIP' })}
              options={cattleProductivityOptions()}
              baseline={defaultStageData.cattleProductivity}
              adornment=" "
              useStyle={false}
              disabled={false}
            />

            {(isGrowingOperation && isBatchGrowingOperation) && <div />}

            <div>
              {(isGrowingOperation && isBatchGrowingOperation) && <Spacer />}
              <DsmButton
                variant="text"
                onClick={() => setOpenDescriptionDialog(true)}
              >
                <DsmIcon slot="before" name="general/eye" />
                {intl.formatMessage({ id: 'DATABASE_FOUNDATION_DEFINITIONS' })}
              </DsmButton>
            </div>

            {!(isGrowingOperation && isBatchGrowingOperation) && <div />}
          </>)}

        {isBatchGrowingOperation && <div />}

        {formType !== FormType.View &&
          <div>
            {(isGrowingOperation && isBatchGrowingOperation && !isProductivityEdit) && <Spacer />}
            <EditModeButtons
              isEditing={isProductivityEdit}
              onEdit={() => setIsProductivityEdit(true)}
              onCancel={productivityCancelHandler}
            />
          </div>}

      </DsmGrid>
    </>
  )
}

export default BeefInputProductivityComponent;
